import { replaceLineBreaks, boldText } from '../util/element';
import { CATEGORY, SERIES, VALUE, POINT, ANNOTATIONS_LINEHEIGHT, ANNOTATIONS_LINEHEIGHT_VIDEO } from '../data/constants';
import getPoint from '../data/getPoint';
import * as format from '../data/format';
import { arrowWidth } from './defs';

const COLOR = '#333';

export function preRender(data, options) {
  const shapeOptions = getShapeOptions();
  const labelOptions = getLabelOptions(options);
  const [labels, shapes, extraSpace] = createAnnotations(options);
  const visible = options.size !== 'marketwatch-ZT';
  if (visible) addSpacing(data, options, extraSpace);
  data.annotations.push({ labels, labelOptions, shapes, shapeOptions, visible });
}

function getShapeOptions() {
  return {
    fill: COLOR,
    stroke: COLOR,
    type: 'circle',
    strokeWidth: 0
  };
}

function getLabelOptions(options) {
  let { fontFamily, fontWeight } = options.styles.get('normal');
  let [fontSize, lineHeight] = getStyles(options);
  let color = options.styles.normal.color;
  if (options.product === 'mansionglobal') {
    color = options.styles.annotations.color;
    fontSize = fontSize.toString() + 'px';
    lineHeight = lineHeight.toString() + 'px';
  }
  if (options.id === 'video') {
    fontFamily = 'Retina Narrow';
    fontWeight = 400;
  }
  return {
    style: { color, fontFamily, fontSize, fontWeight, lineHeight },
    backgroundColor: 'rgba(0, 0, 0, 0)', // defaults to gray, must be made transparent
    borderWidth: 0, // defaults to a border, which must be removed
    padding: 0, // label sits on top line before y applied
    y: setY(options), // margin above top line
    overflow: 'none', // allow labels rendered outside plot area
    allowOverlap: true // keep highcharts from hiding labels; spacing handled in postrender
  };
}

function getStyles(options) {
  const { normal, annotations } = options.styles;
  const [normalStyle, annotationStyle] = [normal, annotations].map(getFontStyles);
  const props = ['fontSize', 'lineHeight'];
  return isWsjDigital(options) ? annotationStyle(props) : normalStyle(props);
}

function isWsjDigital(options) {
  return options.product === 'wsj' && !options.print;
}

function getFontStyles(styles) {
  return function(props) {
    return props.map(prop => parseFloat(styles[prop]));
  }
}

function setY(options) {
  // shift labels up 10px when suffix or recession label enabled
  const { id, axes } = options;
  const { units, showRecessions } = axes;
  const offset = id === 'video' ? 30 : 0;
  const y = units.suffix.length > 2 || showRecessions ?
    -20 : -10;
  return y - offset;
}

function createAnnotations(options) {
  const labels = [], shapes = [];
  const lineHeight = getLineHeight(options);
  const spacing = {};
  let maxHeight = 0;
  const annotations = options.annotations.items;
  
  annotations.forEach(item => {
    // create label
    const { flushAlign, showLine, lineType } = item;
    const style = { flushAlign, showLine, lineType }; // info needed in postrender
    
    const formattedText = formatText(item, options);
    const text = formattedText.join('<br>');
    
    const [x, y, yAxis, endPoints, ms] = getXY(item, options);
    
    const label = { point: { x, y: 0, xAxis: 0 }, text, style }; // position at chart top
    
    // create corresponding shape
    const shape = createShape(item, options, x, y, yAxis);
    
    labels.push(label);
    shapes.push(shape);

    // extra space needed for tallest annotation
    const offset = options.id === 'video' ? 1 : 0;
    const numLines = (lastIdx(formattedText) + offset) * lineHeight;
    maxHeight = Math.max(maxHeight, numLines);
    
    // extra space needed for shape on right or left
    if (item.lineEnd !== 'nothing') {
      findMinMax(options, item, spacing, endPoints, ms);
    }
  })
  const { min, max } = spacing;
  return [labels, shapes, { top: maxHeight, min, max }];
}

function getLineHeight(options) {
  const { id, print, styles, product } = options;
  const wsjHeight = id === 'video' ?
    ANNOTATIONS_LINEHEIGHT_VIDEO : ANNOTATIONS_LINEHEIGHT;
  return product === 'wsj' && !print ?
    wsjHeight : parseFloat(styles.normal.lineHeight);
}

function formatText(item, options) {
  const text = compileText(item, options);
  const textBr = replaceLineBreaks(text);
  const lines = textBr.split('<br>');
  const wsjDigital = isWsjDigital(options);
  const [openTag, closeTag] = wsjDigital ?
    [`<span style="font-weight:500">`, '</span>']
    :
    ['<strong>', '</strong>'];
  const boldAnnotation = boldText();
  return lines.map(line => boldAnnotation(line, openTag, closeTag));
}

function compileText(item, options) {
  return (item.text || '')
    .replace(/\\{/g, '__LEFT_BRACKET__')
    .replace(/\\}/g, '__RIGHT_BRACKET__')
    .replace(CATEGORY, compiledCategory(item, options))
    .replace(SERIES, compiledSeries(item, options))
    .replace(VALUE, compiledValue(item, options))
    .replace(POINT, compiledPoint(item, options))
    .replace(/__LEFT_BRACKET__/g, '{')
    .replace(/__RIGHT_BRACKET__/g, '}');
}

function compiledCategory(item, options) {
  const categories = options.longCategories;
  const attach = item.attach;
  return attach.category.enabled ?
    categories[attach.category.index]
    :
    categories[attach.point.index];
}

function compiledSeries(item, options) {
  const p = item.attach.point;
  if (p.enabled && options.series[p.series]) {
    return options.series[p.series].name || 'n/a';
  }
  return '{series}';
}

function compiledValue(item, options) {
  const p = item.attach.point;
  if (p.enabled && options.series[p.series]) {
    const point = getPoint(options.originalSeries[p.series], p.index);
    return point ? point.y : 'n/a';
  }
  return '{value}';
}

function compiledPoint(item, options) {
  const p = item.attach.point;
  if (p.enabled && options.series[p.series]) {
    const point = getPoint(options.originalSeries[p.series], p.index);
    if (point && isFinite(point.y)) {
      return format.applyFull(point.y, options.tooltips, options.type === 'pie', true, true);
    }
    return 'n/a';
  }
  return '{point}';
}

function getXY(item, options) {  
  const { category, point } = item.attach;
  
  const [seriesIdx, dataIdx] = category.enabled ?
    [0, category.index]
    :
    [point.series, point.index];
  
  // when data dates not in ascending order, use originalSeries to find proper item
  const currentSeries = options.originalSeries[seriesIdx];
  const noIndexProp = !currentSeries[0].hasOwnProperty('index');
  const arrIdx = noIndexProp ?
    dataIdx
    :
    getArrIdx(currentSeries, dataIdx);
  const dataPoint = currentSeries[arrIdx];

  const seriesY = options.type === 'area' ?
    getAreaY(options, seriesIdx, arrIdx) : dataPoint.y;
  
  const [xData, y, yAxis] = category.enabled ?
    [category, undefined, undefined] // shape moved to proper location in postrender
    :
    [dataPoint, seriesY, 0];

  let x;
  if (noIndexProp) x = dataIdx;
  else if (options.categoriesAreDates) x = dataPoint.x;
  else x = xData.index;
  
  const first = arrIdx === 0;
  const last = arrIdx === lastIdx(currentSeries);

  let ms;
  if (options.irregularDates) ms = findMilliseconds(options, currentSeries);

  return [x, y, yAxis, { first, last }, ms];
}

function getArrIdx(currentSeries, dataIdx) {
  return currentSeries.findIndex(point => dataIdx === point.index);
}

function getAreaY(options, seriesIdx, dataIdx) {
  // for area charts, need to calculate top y for each section of stack
  const series = options.originalSeries;
  const totalForPercent = series.reduce((total, currentSeries) => total + currentSeries[dataIdx].y, 0);
  let seriesY = 0;
  for (let i = lastIdx(series); i >= seriesIdx; i--) {
    const { y } = series[i][dataIdx];
    const height = options.stacking === 'normal' ?
      y : y / totalForPercent * 100
    seriesY += height;
  }
  return seriesY;
}

function lastIdx(arr) {
  return arr.length - 1;
}

export function findMilliseconds(options, series) {
  // for highstocks charts, calculate how many milliseconds equal shape's radius
  const delta = findDelta(series);
  const pxPerX = options.width / series.length - 1;
  return delta / pxPerX;
}

function findDelta(series) {
  let prevDelta = 0,
    currentDelta;
  for (let i = 1; i < series.length; i++) {
    currentDelta = series[i].x - series[i - 1].x;
    if (currentDelta === prevDelta) return currentDelta;
    prevDelta = currentDelta;
  }
}

function createShape(item, options, x, y, yAxis) {
  const shape = {};
  const point = { x, y, xAxis: 0, yAxis };
  const { print } = options;
  switch(item.lineEnd) {
    case 'circle':
      shape.r = circleR(print);
      shape.strokeWidth = print ? 0.5 : 1;
      shape.fill = 'rgba(0, 0, 0, 0)';
      shape.point = point;
      break;
    case 'arrow':
      // transparent path ending with custom arrow (created in defs)
      shape.type = 'path';
      shape.markerEnd = 'arrow';
      shape.points = [{ x, y: 0, xAxis: 0 }, { x, y, xAxis: 0, yAxis }];
      break;
    case 'nothing':
      // transparent shape used for drawing line in postrender
      shape.point = point;
      break;
    default: // dot
      shape.r = options.id === 'video' ? 9 : dotR(print);
      shape.fill = options.id === 'video' ? '#fff' : undefined;
      shape.point = point;
  }
  return shape;
}

function circleR(print) {
  return print ? 4 : 6.5;
}

function dotR(print) {
  return print ? 1.5 : 3;
}

function findMinMax(options, item, spacing, endPoints, ms) {
  const { print, irregularDates } = options;
  const { lineEnd } = item;
  const r = getRadius(print, lineEnd);
  if (endPoints.first) {
    spacing.min = r;
  }
  else if (endPoints.last) {
    spacing.max = irregularDates ?
      ms * (r + 2) : r;
  }
}

function getRadius(print, lineEnd) {
  switch (lineEnd) {
    case 'dot':
      return dotR(print);
    case 'circle':
      return circleR(print);
    case 'arrow':
      return arrowWidth(print) / 2;
  }
}

function addSpacing(data, options, extraSpace) {
  data.chart.spacing[0] += extraSpace.top; // for multiline annotations
  // for shapes for 1st or last data point, add sufficient padding
  const padding = setPadding(data, options, extraSpace);
  padding('min', 'max');
}

function setPadding(data, options, extraSpace) {
  return function(...args) {
    args.forEach(type => {
      const space = extraSpace[type];
      const { xAxis } = data;
      const { irregularDates, width } = options;
      if (space) {
        irregularDates ?
          xAxis.overscroll += extraSpace['max'] || 0
          :
          xAxis[`${type}Padding`] = (space + 5) / width;
      }
    })
  }
}


// WEBPACK FOOTER //
// ./src/chart/inner/annotations-prerender.js